import { SxProps, Theme } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { memo, useEffect, useMemo } from 'react';

import { Autocomplete, AutocompleteOption } from '~/components/Autocomplete';
import { Checkbox } from '~/components/Checkbox';
import { DateField } from '~/components/DateField';
import { TextField } from '~/components/TextField';
import { useUpdateEffect } from '~/hooks/useUpdateEffect';
import { useGetCeoEducationsQuery, useGetSecurityTypesQuery } from '~/services/rtkQuery/company';
import { CreateCompanyFormValues } from '~/types/formValues';
import { calculateAcquired } from '~/utils/calculations';

import { Investors } from './components/Investors';
import styles from './styles.module.scss';

const LONG_TEXT_FIELD_SPAN_2_SX: SxProps<Theme> = {
  gridColumn: 'span 2',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};

const LONG_TEXT_FIELD_SPAN_3_SX: SxProps<Theme> = {
  gridColumn: 'span 3',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};

const LONG_TEXT_FIELD_SPAN_4_SX: SxProps<Theme> = {
  gridColumn: 'span 4',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};

interface Props {
  companyLocked: boolean;
  roundIndex: number;
  subroundIndex: number;
  previousRoundSubroundsCount: number;
}

const Component: React.FC<Props> = ({
  companyLocked,
  roundIndex,
  subroundIndex,
  previousRoundSubroundsCount,
}) => {
  const formik = useFormikContext<CreateCompanyFormValues>();
  const ceoEducationsQuery = useGetCeoEducationsQuery();
  const securityTypesQuery = useGetSecurityTypesQuery();
  const subroundFieldPath = `form.rounds.${roundIndex}.subrounds.${subroundIndex}` as const;
  const subroundValues = formik.values.form.rounds[roundIndex].subrounds[subroundIndex];
  // @ts-expect-error nested object type issue
  const subroundErrors = formik.errors.form?.rounds?.[roundIndex]?.subrounds?.[subroundIndex];
  const subroundTouched = formik.touched.form?.rounds?.[roundIndex]?.subrounds?.[subroundIndex];
  const {
    dealSize,
    postMoneyValuation,
    ceoChanged,
    securityType,
    generatingRevenue,
    revenueStatusChanged,
    noAnnouncement,
    acquiredPercent,
  } = subroundValues;
  const previousSubroundGeneratingRevenue =
    roundIndex !== 0
      ? formik.values.form.rounds[subroundIndex === 0 ? roundIndex - 1 : roundIndex].subrounds[
          subroundIndex === 0 ? previousRoundSubroundsCount - 1 : subroundIndex - 1
        ].generatingRevenue
      : null;

  useEffect(() => {
    if (previousSubroundGeneratingRevenue !== null) {
      const generatingRevenueChanged = generatingRevenue !== previousSubroundGeneratingRevenue;

      if (revenueStatusChanged !== generatingRevenueChanged) {
        formik.setFieldValue(
          `${subroundFieldPath}.revenueStatusChanged`,
          generatingRevenueChanged,
          false,
        );
      }
    }
  }, [
    roundIndex,
    generatingRevenue,
    previousSubroundGeneratingRevenue,
    subroundFieldPath,
    revenueStatusChanged,
    formik,
  ]);

  useUpdateEffect(() => {
    if (!(roundIndex === 2 && (securityType?.value === 2 || securityType?.value === 3))) {
      if (dealSize && postMoneyValuation) {
        const acquired = calculateAcquired(dealSize, postMoneyValuation);

        formik.setFieldValue(`${subroundFieldPath}.acquiredPercent`, (acquired * 100).toString());
        formik.setFieldValue(`${subroundFieldPath}.acquiredCalc`, acquired.toString());
      } else {
        formik.setFieldValue(`${subroundFieldPath}.acquiredPercent`, '', false);
        formik.setFieldValue(`${subroundFieldPath}.acquiredCalc`, '', false);
      }
    }
  }, [dealSize, postMoneyValuation, securityType?.value, roundIndex]);

  useUpdateEffect(() => {
    if (noAnnouncement) {
      formik.setFieldValue(`${subroundFieldPath}.announcedDate`, null);
    }
  }, [noAnnouncement]);

  useUpdateEffect(() => {
    if (roundIndex === 2 && (securityType?.value === 2 || securityType?.value === 3)) {
      if (dealSize && acquiredPercent) {
        const postMoneyValuation = parseFloat(dealSize) / (parseFloat(acquiredPercent) / 100);
        const safePostMoneyValuation = Number.isFinite(postMoneyValuation)
          ? postMoneyValuation.toString()
          : '0';

        const preMoneyValuation = postMoneyValuation - parseFloat(dealSize);
        const safePreMoneyValuation = Number.isFinite(preMoneyValuation)
          ? preMoneyValuation.toString()
          : '0';

        formik.setFieldValue(`${subroundFieldPath}.postMoneyValuation`, safePostMoneyValuation);
        formik.setFieldValue(`${subroundFieldPath}.preMoneyValuation`, safePreMoneyValuation);
      } else {
        formik.setFieldValue(`${subroundFieldPath}.postMoneyValuation`, '', false);
        formik.setFieldValue(`${subroundFieldPath}.preMoneyValuation`, '', false);
      }
    }
  }, [roundIndex, securityType?.value, dealSize, acquiredPercent]);

  const ceoEducationOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      ceoEducationsQuery.data?.result.data.map(ceoEducation => ({
        label: ceoEducation.name,
        value: ceoEducation.id,
      })) || []
    );
  }, [ceoEducationsQuery.data]);

  const securityTypeOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      securityTypesQuery.data?.result.data.map(securityType => ({
        label: securityType.name,
        value: securityType.id,
      })) || []
    );
  }, [securityTypesQuery.data]);

  return (
    <div className={styles.subround}>
      <div className={styles.subround__fields}>
        <DateField
          disabled={companyLocked}
          error={subroundTouched?.dealDate && !!subroundErrors?.dealDate}
          helperText={subroundTouched?.dealDate ? subroundErrors?.dealDate : undefined}
          label="Deal Date*"
          value={subroundValues.dealDate}
          onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.dealDate`)}
          onChange={date => formik.setFieldValue(`${subroundFieldPath}.dealDate`, date)}
        />
        <DateField
          disabled={companyLocked || noAnnouncement}
          emptyDatePlaceholder={subroundValues.noAnnouncement ? 'No announcement' : undefined}
          error={subroundTouched?.announcedDate && !!subroundErrors?.announcedDate}
          helperText={subroundTouched?.announcedDate ? subroundErrors?.announcedDate : undefined}
          label="Announced Date*"
          value={subroundValues.announcedDate}
          onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.announcedDate`)}
          onChange={date => formik.setFieldValue(`${subroundFieldPath}.announcedDate`, date)}
        />
        <TextField
          disabled={companyLocked}
          error={subroundTouched?.dealSize && !!subroundErrors?.dealSize}
          helperText={subroundTouched?.dealSize ? subroundErrors?.dealSize : undefined}
          label="Deal Size*"
          name={`${subroundFieldPath}.dealSize`}
          type="number"
          value={subroundValues.dealSize}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        {roundIndex === 2 && (
          <Autocomplete
            disabled={companyLocked}
            error={subroundTouched?.securityType && !!subroundErrors?.securityType}
            helperText={subroundTouched?.securityType ? subroundErrors?.securityType : undefined}
            options={securityTypeOptions}
            placeholder="Security Type*"
            value={subroundValues.securityType}
            onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.securityType`)}
            onChange={value => formik.setFieldValue(`${subroundFieldPath}.securityType`, value)}
          />
        )}
        {roundIndex === 2 && (securityType?.value === 2 || securityType?.value === 3) && (
          <>
            <TextField
              disabled={companyLocked}
              error={subroundTouched?.cap && !!subroundErrors?.cap}
              helperText={subroundTouched?.cap ? subroundErrors?.cap : undefined}
              label="CAP*"
              name={`${subroundFieldPath}.cap`}
              type="number"
              value={subroundValues.cap}
              variant="outlined"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
            <TextField
              disabled={companyLocked}
              error={subroundTouched?.discount && !!subroundErrors?.discount}
              helperText={subroundTouched?.discount ? subroundErrors?.discount : undefined}
              label="%Discount*"
              name={`${subroundFieldPath}.discount`}
              type="number"
              value={subroundValues.discount}
              variant="outlined"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </>
        )}
        {roundIndex === 2 && securityType?.value === 3 && (
          <>
            <TextField
              disabled={companyLocked}
              error={subroundTouched?.interest && !!subroundErrors?.interest}
              helperText={subroundTouched?.interest ? subroundErrors?.interest : undefined}
              label="%Interest*"
              name={`${subroundFieldPath}.interest`}
              type="number"
              value={subroundValues.interest}
              variant="outlined"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
            <TextField
              disabled
              error={subroundTouched?.conversionAmount && !!subroundErrors?.conversionAmount}
              helperText={
                subroundTouched?.conversionAmount ? subroundErrors?.conversionAmount : undefined
              }
              label="Conversion Amount"
              name={`${subroundFieldPath}.conversionAmount`}
              type="number"
              value={subroundValues.conversionAmount}
              variant="outlined"
            />
          </>
        )}
        <TextField
          disabled={
            companyLocked ||
            (roundIndex === 2 && (securityType?.value === 2 || securityType?.value === 3))
          }
          error={subroundTouched?.preMoneyValuation && !!subroundErrors?.preMoneyValuation}
          helperText={
            subroundTouched?.preMoneyValuation ? subroundErrors?.preMoneyValuation : undefined
          }
          label={`${
            roundIndex === 2 && (securityType?.value === 2 || securityType?.value === 3)
              ? 'Effective '
              : ''
          }Pre-Money Valuation*`}
          name={`${subroundFieldPath}.preMoneyValuation`}
          type="number"
          value={subroundValues.preMoneyValuation}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <TextField
          disabled={
            companyLocked ||
            (roundIndex === 2 && (securityType?.value === 2 || securityType?.value === 3))
          }
          error={subroundTouched?.postMoneyValuation && !!subroundErrors?.postMoneyValuation}
          helperText={
            subroundTouched?.postMoneyValuation ? subroundErrors?.postMoneyValuation : undefined
          }
          label={`${
            roundIndex === 2 && (securityType?.value === 2 || securityType?.value === 3)
              ? 'Effective '
              : ''
          }Post Money Valuation*`}
          name={`${subroundFieldPath}.postMoneyValuation`}
          type="number"
          value={subroundValues.postMoneyValuation}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <TextField
          disabled={companyLocked}
          error={subroundTouched?.acquiredPercent && !!subroundErrors?.acquiredPercent}
          helperText={
            subroundTouched?.acquiredPercent ? subroundErrors?.acquiredPercent : undefined
          }
          label="%Acquired*"
          name={`${subroundFieldPath}.acquiredPercent`}
          type="number"
          value={subroundValues.acquiredPercent}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <TextField
          disabled
          error={subroundTouched?.acquiredCalc && !!subroundErrors?.acquiredCalc}
          helperText={subroundTouched?.acquiredCalc ? subroundErrors?.acquiredCalc : undefined}
          label="Acquired (calc)"
          name={`${subroundFieldPath}.acquiredCalc`}
          type="number"
          value={subroundValues.acquiredCalc}
          variant="outlined"
        />
        <TextField
          disabled={companyLocked}
          error={subroundTouched?.employees && !!subroundErrors?.employees}
          helperText={subroundTouched?.employees ? subroundErrors?.employees : undefined}
          label="Employees*"
          name={`${subroundFieldPath}.employees`}
          type="number"
          value={subroundValues.employees}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        {(roundIndex === 0 || ceoChanged) && (
          <>
            <TextField
              disabled={companyLocked}
              error={subroundTouched?.ceo?.name && !!subroundErrors?.ceo?.name}
              helperText={subroundTouched?.ceo?.name ? subroundErrors?.ceo?.name : undefined}
              label="CEO Name*"
              name={`${subroundFieldPath}.ceo.name`}
              value={subroundValues.ceo.name}
              variant="outlined"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
            <Autocomplete
              disabled={companyLocked}
              error={subroundTouched?.ceo?.education && !!subroundErrors?.ceo?.education}
              helperText={
                subroundTouched?.ceo?.education ? subroundErrors?.ceo?.education : undefined
              }
              options={ceoEducationOptions}
              placeholder="CEO Education*"
              value={subroundValues.ceo.education}
              onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.ceo.education`)}
              onChange={value => formik.setFieldValue(`${subroundFieldPath}.ceo.education`, value)}
            />
            <TextField
              disabled={companyLocked}
              error={
                subroundTouched?.ceo?.educationHistory && !!subroundErrors?.ceo?.educationHistory
              }
              helperText={
                subroundTouched?.ceo?.educationHistory
                  ? subroundErrors?.ceo?.educationHistory
                  : undefined
              }
              label="CEO Education Details"
              name={`${subroundFieldPath}.ceo.educationHistory`}
              sx={
                roundIndex === 2
                  ? securityType?.value === 2
                    ? LONG_TEXT_FIELD_SPAN_3_SX
                    : LONG_TEXT_FIELD_SPAN_4_SX
                  : LONG_TEXT_FIELD_SPAN_2_SX
              }
              title={subroundValues.ceo.educationHistory}
              value={subroundValues.ceo.educationHistory}
              variant="outlined"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
            <TextField
              disabled={companyLocked}
              error={subroundTouched?.ceo?.biography && !!subroundErrors?.ceo?.biography}
              helperText={
                subroundTouched?.ceo?.biography ? subroundErrors?.ceo?.biography : undefined
              }
              label="CEO Biography"
              name={`${subroundFieldPath}.ceo.biography`}
              sx={LONG_TEXT_FIELD_SPAN_4_SX}
              title={subroundValues.ceo.biography}
              value={subroundValues.ceo.biography}
              variant="outlined"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </>
        )}
      </div>
      <div className={styles.subround__checkboxes}>
        {roundIndex === 0 ? (
          <Checkbox
            checked={subroundValues.ceoIsFounder}
            containerClassName={styles.subround__checkboxes__checkbox}
            disabled={companyLocked}
            label="CEO is a Founder"
            onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.ceoIsFounder`)}
            onChange={checked => formik.setFieldValue(`${subroundFieldPath}.ceoIsFounder`, checked)}
          />
        ) : (
          <Checkbox
            checked={subroundValues.ceoChanged}
            containerClassName={styles.subround__checkboxes__checkbox}
            disabled={companyLocked}
            label="CEO Changed"
            onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.ceoChanged`)}
            onChange={checked => formik.setFieldValue(`${subroundFieldPath}.ceoChanged`, checked)}
          />
        )}
        <Checkbox
          checked={subroundValues.generatingRevenue}
          containerClassName={styles.subround__checkboxes__checkbox}
          disabled={companyLocked}
          label="Generating Revenue"
          onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.generatingRevenue`)}
          onChange={checked =>
            formik.setFieldValue(`${subroundFieldPath}.generatingRevenue`, checked)
          }
        />
        <Checkbox
          checked={subroundValues.participatingPreferred}
          containerClassName={styles.subround__checkboxes__checkbox}
          disabled={companyLocked}
          label="Participating Preferred"
          onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.participatingPreferred`)}
          onChange={checked =>
            formik.setFieldValue(`${subroundFieldPath}.participatingPreferred`, checked)
          }
        />
        {roundIndex !== 0 && (
          <>
            <Checkbox
              disabled
              checked={subroundValues.revenueStatusChanged}
              containerClassName={styles.subround__checkboxes__checkbox}
              label="Revenue Status changed"
              name={`${subroundFieldPath}.revenueStatusChanged`}
            />
            <Checkbox
              checked={subroundValues.generatingProfit}
              containerClassName={styles.subround__checkboxes__checkbox}
              disabled={companyLocked}
              label="Generating Profit"
              onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.generatingProfit`)}
              onChange={checked =>
                formik.setFieldValue(`${subroundFieldPath}.generatingProfit`, checked)
              }
            />
          </>
        )}
        <Checkbox
          checked={subroundValues.noAnnouncement}
          containerClassName={styles.subround__checkboxes__checkbox}
          disabled={companyLocked}
          label="No announcement"
          onBlur={() => formik.setFieldTouched(`${subroundFieldPath}.noAnnouncement`)}
          onChange={checked => formik.setFieldValue(`${subroundFieldPath}.noAnnouncement`, checked)}
        />
      </div>
      <Investors
        companyLocked={companyLocked}
        roundIndex={roundIndex}
        subroundIndex={subroundIndex}
      />
    </div>
  );
};

export const Subround = memo(Component);
